import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ReserveHeader } from "@/components/ReserveHeader";
import { VirtualList } from "@/components/VirtualList";
import { ListRouteSkeleton } from "@/components/RouteSkeleton";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { submitReview } from "@/lib/reviews.functions";

type ShiftStatus = "open" | "awaiting_payment" | "paid" | "filled" | "completed" | "cancelled" | "disputed";

type Shift = {
  id: string;
  start_time: string;
  end_time: string;
  hourly_rate: number;
  status: ShiftStatus;
  address: string | null;
  postcode: string | null;
  venue_id: string;
  completed_at: string | null;
  cancelled_at: string | null;
  auto_confirmed: boolean;
};

type Payout = {
  id: string;
  shift_id: string;
  amount_pence: number;
  status: string;
  created_at: string;
};

type Review = {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
};

type VenueProfile = {
  id: string;
  full_name: string | null;
  is_verified_business?: boolean | null;
};

type Profile = {
  id: string;
  full_name: string | null;
  is_verified: boolean;
  has_dbs: boolean;
};

export const Route = createFileRoute("/_authenticated/history")({
  head: () => ({ meta: [{ title: "History — The Hospitality Reserve" }] }),
  component: HistoryPage,
});

function HistoryPage() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [venues, setVenues] = useState<Record<string, VenueProfile>>({});
  const [loading, setLoading] = useState(true);
  const [myVenueReviewShiftIds, setMyVenueReviewShiftIds] = useState<Set<string>>(new Set());
  const [reviewShiftId, setReviewShiftId] = useState<string | null>(null);
  const reviewFn = useServerFn(submitReview);

  const load = async () => {
    setLoading(true);
    const { data: { session: __s } } = await supabase.auth.getSession(); const u = { user: __s?.user ?? null };
    if (!u.user) return;

    const [{ data: p }, { data: s }, { data: po }, { data: r }, { data: myV }] = await Promise.all([
      supabase.from("profiles").select("id,full_name,is_verified,has_dbs").eq("id", u.user.id).maybeSingle(),
      supabase
        .from("shifts")
        .select("id,start_time,end_time,hourly_rate,status,address,postcode,venue_id,completed_at,cancelled_at,auto_confirmed")
        .eq("bartender_id", u.user.id)
        .in("status", ["completed", "cancelled", "disputed"])
        .order("start_time", { ascending: false }),
      supabase.from("payouts").select("id,shift_id,amount_pence,status,created_at").eq("bartender_id", u.user.id),
      supabase.from("reviews").select("id,rating,comment,created_at").eq("bartender_id", u.user.id).eq("direction", "venue_to_bartender").order("created_at", { ascending: false }),
      supabase.from("reviews").select("shift_id").eq("bartender_id", u.user.id).eq("direction", "bartender_to_venue"),
    ]);

    setProfile(p as Profile);
    setShifts((s ?? []) as Shift[]);
    setPayouts((po ?? []) as Payout[]);
    setReviews((r ?? []) as Review[]);
    setMyVenueReviewShiftIds(new Set(((myV ?? []) as Array<{ shift_id: string }>).map((x) => x.shift_id)));

    const venueIds = Array.from(new Set((s ?? []).map((shift) => shift.venue_id).filter(Boolean)));
    if (venueIds.length) {
      const { data: venueProfiles } = await supabase
        .from("profiles")
        .select("id,full_name,is_verified_business")
        .in("id", venueIds);
      const map: Record<string, VenueProfile> = {};
      (venueProfiles ?? []).forEach((v) => { map[v.id] = v as VenueProfile; });
      setVenues(map);
    }

    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const completedShifts = shifts.filter((s) => s.status === "completed");
  const cancelledShifts = shifts.filter((s) => s.status === "cancelled");
  const disputedShifts = shifts.filter((s) => s.status === "disputed");

  const totalEarned = payouts
    .filter((p) => p.status === "payable" || p.status === "paid_out")
    .reduce((sum, p) => sum + p.amount_pence, 0);

  const avgRating = reviews.length
    ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  const shiftEarnings = (shiftId: string) => {
    const payout = payouts.find((p) => p.shift_id === shiftId);
    return payout ? payout.amount_pence : 0;
  };

  const shiftDuration = (start: string, end: string) => {
    const hours = (new Date(end).getTime() - new Date(start).getTime()) / 3_600_000;
    return `${hours.toFixed(1)}h`;
  };

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250);

  const filteredShifts = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return shifts;
    return shifts.filter((s) => {
      const venueName = venues[s.venue_id]?.full_name?.toLowerCase() ?? "";
      const addr = `${s.address ?? ""} ${s.postcode ?? ""}`.toLowerCase();
      const date = new Date(s.start_time).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).toLowerCase();
      return venueName.includes(q) || addr.includes(q) || date.includes(q) || s.status.includes(q);
    });
  }, [shifts, venues, debouncedSearch]);

  if (loading) return <ListRouteSkeleton rows={6} />;

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="bartender" />
      <main className="mx-auto max-w-7xl px-6 py-10 grid gap-10 lg:grid-cols-[320px_1fr]">
        <aside>
          <p className="tracking-wide-luxury text-accent">Member</p>
          <h1 className="mt-3 font-display text-3xl">{profile?.full_name || "Your Record"}</h1>
          <span className="gold-divider mt-4" />

          <div className="mt-6 space-y-3">
            <div className="text-xs tracking-wide-luxury text-muted-foreground space-y-1.5 pt-2">
              <p>· {profile?.is_verified ? "Identity Verified" : "Identity Unverified"}</p>
              <p>· {profile?.has_dbs ? "DBS on File" : "DBS Not on File"}</p>
            </div>
          </div>

          <div className="mt-8 border border-border bg-card p-5">
            <p className="font-display text-lg">Summary</p>
            <span className="gold-divider mt-3" />
            <div className="mt-4 text-sm text-foreground/80 space-y-2">
              <p>
                Completed: <span className="font-display text-accent">{completedShifts.length}</span>
              </p>
              <p>
                Cancelled: <span className="font-display">{cancelledShifts.length}</span>
              </p>
              <p>
                Disputed: <span className="font-display">{disputedShifts.length}</span>
              </p>
              <p>
                Lifetime earnings: <span className="font-display text-accent">£{(totalEarned / 100).toFixed(2)}</span>
              </p>
              {avgRating && (
                <p>
                  Average rating: <span className="font-display text-accent">{avgRating} ★</span>
                  <span className="text-muted-foreground"> · {reviews.length} review{reviews.length === 1 ? "" : "s"}</span>
                </p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <Link
              to="/bartender"
              className="inline-block border border-border px-4 py-2 text-xs tracking-wide-luxury text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
            >
              ← Back to Feed
            </Link>
          </div>
        </aside>

        <section>
          <p className="tracking-wide-luxury text-accent">Record</p>
          <h2 className="mt-3 font-display text-3xl">Engagement History</h2>
          <span className="gold-divider mt-4" />

          {shifts.length > 0 && (
            <div className="mt-6">
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by venue, date, address, or status…"
                className="rounded-none"
              />
              <p className="mt-2 text-[0.65rem] tracking-wide-luxury text-muted-foreground">
                {filteredShifts.length} of {shifts.length} engagement{shifts.length === 1 ? "" : "s"}
              </p>
            </div>
          )}

          {shifts.length === 0 && (
            <div className="mt-8 border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              No completed or cancelled engagements yet.
            </div>
          )}

          {shifts.length > 0 && filteredShifts.length === 0 && (
            <div className="mt-8 border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              No engagements match “{debouncedSearch}”.
            </div>
          )}

          {filteredShifts.length > 0 && (() => {
            const renderShift = (s: Shift) => {
              const venue = venues[s.venue_id];
              const earnings = shiftEarnings(s.id);
              const isCompleted = s.status === "completed";
              const isDisputed = s.status === "disputed";
              return (
                <article
                  className={`border bg-card p-6 mb-4 ${isCompleted ? "border-border" : isDisputed ? "border-red-700/30" : "border-border"}`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-display text-xl">{formatDate(s.start_time)}</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {formatTime(s.start_time)} — {formatTime(s.end_time)} · {shiftDuration(s.start_time, s.end_time)}
                      </p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {[s.address, s.postcode].filter(Boolean).join(" · ") || "No address on file"}
                      </p>
                      <p className="mt-1 text-xs tracking-wide-luxury text-muted-foreground">
                        Venue · {venue?.full_name || "Unknown"}
                        {venue?.is_verified_business && (
                          <span className="ml-2 inline-block border border-emerald-700 text-emerald-700 px-1.5 py-0.5 text-[0.55rem]">✓ Verified business</span>
                        )}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <span
                        className={`inline-block border px-3 py-1 tracking-wide-luxury text-[0.65rem] ${
                          isCompleted
                            ? "text-emerald-700 border-emerald-700"
                            : isDisputed
                              ? "text-red-700 border-red-700"
                              : "text-muted-foreground border-border line-through"
                        }`}
                      >
                        {isCompleted ? "Completed" : isDisputed ? "Disputed" : "Cancelled"}
                      </span>
                      {isCompleted && (
                        <p className="mt-2 font-display text-lg text-accent">
                          £{(earnings / 100).toFixed(2)}
                        </p>
                      )}
                      {s.auto_confirmed && (
                        <p className="mt-1 text-[0.6rem] text-muted-foreground">Auto-confirmed</p>
                      )}
                    </div>
                  </div>
                  {earnings > 0 && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Rate: £{s.hourly_rate.toFixed(2)}/hr · Earnings: £{(earnings / 100).toFixed(2)}
                    </p>
                  )}
                  {isCompleted && !myVenueReviewShiftIds.has(s.id) && (
                    <div className="mt-4">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none tracking-wide-luxury"
                        onClick={() => setReviewShiftId(s.id)}
                      >
                        Rate venue
                      </Button>
                    </div>
                  )}
                  {isCompleted && myVenueReviewShiftIds.has(s.id) && (
                    <p className="mt-3 text-[0.65rem] tracking-wide-luxury text-muted-foreground">Your venue review submitted</p>
                  )}
                </article>
              );
            };

            // Virtualize only when the list is long enough to matter; otherwise
            // a normal flow keeps page-level scroll natural.
            if (filteredShifts.length > 20) {
              return (
                <div className="mt-8">
                  <VirtualList
                    items={filteredShifts}
                    estimateSize={210}
                    height="75vh"
                    getKey={(s) => s.id}
                    renderItem={(s) => renderShift(s)}
                  />
                </div>
              );
            }

            return (
              <div className="mt-8 space-y-4">
                {filteredShifts.map((s) => (
                  <div key={s.id}>{renderShift(s)}</div>
                ))}
              </div>
            );
          })()}

          {reviews.length > 0 && (
            <div className="mt-12">
              <p className="tracking-wide-luxury text-accent">Feedback</p>
              <h3 className="mt-3 font-display text-2xl">Reviews Received</h3>
              <span className="gold-divider mt-4" />
              <div className="mt-6 space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} className="border border-border bg-card p-4">
                    <p className="text-accent text-xs tracking-wide-luxury">
                      {"★".repeat(r.rating)}{"·".repeat(5 - r.rating)}
                    </p>
                    {r.comment && <p className="mt-2 text-sm text-foreground/80">{r.comment}</p>}
                    <p className="mt-2 text-[0.6rem] text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString("en-GB")}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>

      <RateVenueDialog
        shiftId={reviewShiftId}
        onClose={() => setReviewShiftId(null)}
        onSubmitted={() => { setReviewShiftId(null); void load(); }}
        submit={reviewFn}
      />
    </div>
  );
}

function RateVenueDialog({
  shiftId, onClose, onSubmitted, submit,
}: {
  shiftId: string | null;
  onClose: () => void;
  onSubmitted: () => void;
  submit: (args: { data: { shift_id: string; rating: number; comment?: string; direction: "bartender_to_venue" } }) => Promise<unknown>;
}) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (shiftId) { setRating(5); setComment(""); }
  }, [shiftId]);

  const handle = async () => {
    if (!shiftId) return;
    setBusy(true);
    try {
      await submit({ data: { shift_id: shiftId, rating, comment: comment.trim() || undefined, direction: "bartender_to_venue" } });
      toast.success("Thanks — your review of the venue is in.");
      onSubmitted();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not submit review.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!shiftId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Rate the Venue</DialogTitle>
          <DialogDescription>Your honest feedback keeps the register's standards high.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-xs tracking-wide-luxury text-muted-foreground">Rating</p>
            <div className="mt-2 flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  className={`h-9 w-9 border text-sm ${rating >= n ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground"}`}
                  aria-label={`${n} star${n === 1 ? "" : "s"}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs tracking-wide-luxury text-muted-foreground">Comment (optional)</p>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} className="mt-2 rounded-none" />
          </div>
          <Button
            type="button"
            onClick={handle}
            disabled={busy}
            className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury py-6"
          >
            {busy ? "Submitting…" : "Submit review"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
